import { Info, HelpCircle, AlertCircle, Settings, User, FileText } from 'lucide-react';
import { Tooltip, Icon, Text } from '@components/base/display';
import { Button } from '@components/base/actions';
import { VStack, HStack } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

const positions = ['top', 'bottom', 'left', 'right'] as const;

function TooltipShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="tooltip">
          <Tooltip
            content="This is a helpful tooltip message"
            position={playgroundProps.position as 'top' | 'bottom' | 'left' | 'right'}
            delay={playgroundProps.delay as number}
          >
            <Button>Hover me</Button>
          </Tooltip>
      </ShowcasePreviewSection>

      {/* Position Variants */}
      <ShowcaseDemoSection
        title="Position Variants"
        description="Tooltips can appear on any side of the trigger element"
       
      >
          <HStack gap={6} wrap="wrap" className="justify-center py-8">
            {positions.map((position) => (
              <Tooltip key={position} content={`Tooltip on ${position}`} position={position}>
                <Button variant="secondary">{position}</Button>
              </Tooltip>
            ))}
          </HStack>
      </ShowcaseDemoSection>

      {/* Different Triggers */}
      <ShowcaseDemoSection
        title="Different Trigger Elements"
        description="Tooltips can be attached to any focusable or interactive element"
       
      >
          <VStack gap={6}>
            {/* With Buttons */}
            <VStack gap={2}>
              <Text size="sm" weight="medium">
                With Buttons
              </Text>
              <HStack gap={4}>
                <Tooltip content="Click to submit your form">
                  <Button>Submit</Button>
                </Tooltip>
                <Tooltip content="Delete this item permanently">
                  <Button variant="danger">Delete</Button>
                </Tooltip>
                <Tooltip content="Settings and preferences">
                  <Button variant="ghost" aria-label="Settings">
                    <Icon icon={Settings} size="md" />
                  </Button>
                </Tooltip>
              </HStack>
            </VStack>

            {/* With Icons */}
            <VStack gap={2}>
              <Text size="sm" weight="medium">
                With Info Icons
              </Text>
              <HStack gap={4}>
                <Tooltip content="Learn more about this feature">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                  >
                    <Icon icon={Info} size="md" color="primary" />
                  </button>
                </Tooltip>
                <Tooltip content="Get help with this section">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                  >
                    <Icon icon={HelpCircle} size="md" color="muted" />
                  </button>
                </Tooltip>
                <Tooltip content="Important notice - please read">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                  >
                    <Icon icon={AlertCircle} size="md" color="warning" />
                  </button>
                </Tooltip>
              </HStack>
            </VStack>

            {/* With Text/Links */}
            <VStack gap={2}>
              <Text size="sm" weight="medium">
                With Text Elements
              </Text>
              <Text>
                Read the{' '}
                <Tooltip content="View the complete terms and conditions document">
                  <button
                    type="button"
                    className="text-[#334E68] underline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                  >
                    terms and conditions
                  </button>
                </Tooltip>{' '}
                before proceeding.
              </Text>
            </VStack>
          </VStack>
      </ShowcaseDemoSection>

      {/* Delay Configuration */}
      <ShowcaseDemoSection
        title="Delay Configuration"
        description="Customize how long before the tooltip appears"
       
      >
          <HStack gap={4} wrap="wrap">
            <Tooltip content="No delay (0ms)" delay={0}>
              <Button variant="secondary">Instant</Button>
            </Tooltip>
            <Tooltip content="Short delay (150ms)" delay={150}>
              <Button variant="secondary">150ms</Button>
            </Tooltip>
            <Tooltip content="Default delay (300ms)" delay={300}>
              <Button variant="secondary">300ms (default)</Button>
            </Tooltip>
            <Tooltip content="Long delay (500ms)" delay={500}>
              <Button variant="secondary">500ms</Button>
            </Tooltip>
          </HStack>
      </ShowcaseDemoSection>

      {/* Rich Content */}
      <ShowcaseDemoSection
        title="Rich Content"
        description="Tooltips can contain formatted text and multiple lines"
       
      >
          <HStack gap={4}>
            <Tooltip
              content={
                <span>
                  <strong>Pro tip:</strong> Use keyboard shortcuts for faster navigation
                </span>
              }
            >
              <Button variant="secondary">With bold text</Button>
            </Tooltip>
            <Tooltip
              content={
                <span>
                  Line 1: First item
                  <br />
                  Line 2: Second item
                  <br />
                  Line 3: Third item
                </span>
              }
            >
              <Button variant="secondary">Multi-line</Button>
            </Tooltip>
          </HStack>
      </ShowcaseDemoSection>

      {/* Insurance Domain Examples */}
      <ShowcaseDemoSection
        title="Insurance Domain Examples"
        description="Common tooltip usage in insurance applications"
       
      >
          <VStack gap={6}>
            {/* Form field help */}
            <VStack gap={2}>
              <Text size="sm" weight="medium">
                Form Field Help
              </Text>
              <HStack gap={2} align="center">
                <Text as="label" size="sm">
                  Policy Number
                </Text>
                <Tooltip content="Your policy number can be found on your declarations page or insurance card. Format: XXX-XXXXXXX">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                  >
                    <Icon icon={HelpCircle} size="sm" color="muted" />
                  </button>
                </Tooltip>
              </HStack>
            </VStack>

            {/* Coverage explanations */}
            <VStack gap={2}>
              <Text size="sm" weight="medium">
                Coverage Explanations
              </Text>
              <HStack gap={4}>
                <Tooltip
                  content="Bodily Injury Liability: Covers injuries you cause to others in an accident"
                  position="bottom"
                >
                  <Button variant="ghost" className="text-sm">
                    BI Liability
                  </Button>
                </Tooltip>
                <Tooltip
                  content="Property Damage Liability: Covers damage you cause to others' property"
                  position="bottom"
                >
                  <Button variant="ghost" className="text-sm">
                    PD Liability
                  </Button>
                </Tooltip>
                <Tooltip
                  content="Comprehensive: Covers non-collision events like theft, vandalism, weather"
                  position="bottom"
                >
                  <Button variant="ghost" className="text-sm">
                    Comprehensive
                  </Button>
                </Tooltip>
              </HStack>
            </VStack>

            {/* Status explanations */}
            <VStack gap={2}>
              <Text size="sm" weight="medium">
                Status Indicators
              </Text>
              <HStack gap={4} align="center">
                <HStack gap={1} align="center">
                  <Icon icon={User} size="sm" />
                  <Text size="sm">Agent</Text>
                  <Tooltip content="Your assigned agent: Michael Chen. Contact for policy questions or changes.">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                    >
                      <Icon icon={Info} size="xs" color="muted" />
                    </button>
                  </Tooltip>
                </HStack>
                <HStack gap={1} align="center">
                  <Icon icon={FileText} size="sm" />
                  <Text size="sm">Documents</Text>
                  <Tooltip content="3 documents require your review: Declaration Page, Policy Schedule, ID Card">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                    >
                      <Icon icon={AlertCircle} size="xs" color="warning" />
                    </button>
                  </Tooltip>
                </HStack>
              </HStack>
            </VStack>
          </VStack>
      </ShowcaseDemoSection>

      {/* Disabled State */}
      <ShowcaseDemoSection
        title="Disabled Tooltip"
        description="Tooltips can be conditionally disabled"
       
      >
          <HStack gap={4}>
            <Tooltip content="This tooltip is enabled">
              <Button variant="secondary">Enabled</Button>
            </Tooltip>
            <Tooltip content="This tooltip won't show" disabled>
              <Button variant="secondary">Disabled tooltip</Button>
            </Tooltip>
          </HStack>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const TooltipShowcase: ShowcaseDefinition = {
  id: 'tooltip',
  name: 'Tooltip',
  category: 'display',
  description: 'Contextual information popup on hover or focus',
  propsDefinition: [
    {
      name: 'position',
      type: 'select',
      options: ['top', 'bottom', 'left', 'right'],
      defaultValue: 'top',
      description: 'Position of the tooltip relative to the trigger',
    },
    {
      name: 'delay',
      type: 'number',
      defaultValue: 300,
      description: 'Delay in milliseconds before showing the tooltip',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'role="tooltip" on the tooltip element',
      'aria-describedby links the trigger to the tooltip',
      'Auto-generated unique ID for the tooltip',
    ],
    keyboardNav: [
      'Focus: Shows tooltip after delay',
      'Blur: Hides tooltip immediately',
      'Escape: Dismisses the tooltip',
    ],
    notes: [
      'Fully accessible via keyboard focus',
      'Screen readers announce tooltip content via aria-describedby',
      'Tooltip remains visible while trigger has focus',
    ],
  },
  component: TooltipShowcaseComponent,
};
